<template>
  <v-container fluid justify-start>
      <loading
      slot="activator"
      :active.sync="isDeleting"
      :can-cancel="false"
      loader="dots"
      :color="appColor_Progress"
    />

    <!-- Data Table -->
    <v-data-table
      :headers="headers"
      :items="filteredResidents"
      :search="search"
      :custom-filter="customFilter"
      :loading="isLoading"
      :no-data-text="noDataText"
      item-key="id"
      class="elevation-2"
      >
      <v-progress-linear slot="progress" :color="appColor_Progress" indeterminate />

      <template v-slot:top>
        
        <!-- Header Toolbar -->
        <v-toolbar dense flat dark :color="toolbarColor">

          <app-back-button v-bind:color="toolbarColorDark" />

          <v-toolbar-title :class="titleClass">{{ title }}</v-toolbar-title>

          <v-spacer />

          <v-toolbar-items>

            <!-- Resident Type Selector -->
            <v-dialog v-model="residentTypeSelectorDialog" max-width="210px" persistent>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-if="isSmallDevice"
                  small
                  slot="activator"
                  :color="appColor_Dark"
                  class="mb-1 mt-0 mr-0 elevation-0"
                  :disabled="isLoading"
                  v-on="on"
                  >New
                </v-btn>
                <v-btn v-else
                  slot="activator"
                  :color="appColor_Dark"
                  :disabled="isLoading"
                  class="mb-1 mt-0 mr-0 elevation-0"
                  v-on="on"
                  >Create Resident
                </v-btn>
              </template>
              <resident-type-selector
                v-on:close="closeChooseResidentType"
                v-bind:toolbarColor="appColor_Medium"
                v-bind:toolbarColorDark="appColor_Dark"
                v-bind:label="'resident type'"
              />
            </v-dialog>
          </v-toolbar-items>
        </v-toolbar>

        <!-- Filters -->
        <template>
          
          <v-row no-gutters class="mx-3 mt-2">
            <v-col v-if="showKioskType" cols="12" sm="2">

              <!-- Kiosk Type -->
              <v-select
                v-model="kioskTypeId"
                item-value="id"
                item-text="name"
                :items="kioskTypesForFilter"
                label="Type"
                placeholder="Type"
                :disabled="disableKioskTypeFilter"
                @change="kioskTypeFilterChanged"
                class="mr-3"
              />
            </v-col>

            <!-- kiosk -->
            <v-col v-if="showKiosk" cols="12" sm="4">
              <kiosk-combobox
                v-model="kioskId"
                :options="filteredKiosksForFilter"
                :label="getExpression('Label', 'Kiosk')"
                :noSelectionLabel="'(All)'"
                :disabled="disableKioskFilter || isLoadingKioskOptions"
                class="pr-3"
              />
            </v-col>

            <!-- Search -->
            <v-col cols="12" :sm="searchColWidth">
              <v-text-field
                v-model="search"
                append-icon="search"
                label="Search"
                single-line
                hide-details
                :disabled="isLoading"
                clearable
              />
            </v-col>
          </v-row>
        </template>
      </template>

      <template v-slot:item.picture="{ item }">
        <span class="text-center">
          <router-link :to="currentRoute">
            <span @click="edit(item)">
              <v-img width="50" height="50" :src="item.picture"></v-img>
            </span>
          </router-link>
        </span>
      </template>

      <template v-slot:item.firstName="{ item }">
        <span class="text-left">
          <span @click="edit(item)" style="white-space: nowrap">
            <span :style="appStyle_HyperLink">{{ item.firstName }}</span>
            <v-icon small class="ml-2" style="cursor: pointer">edit</v-icon>
          </span>
        </span>
      </template>

      <template v-slot:item.lastName="{ item }">
        <span class="text-left">
          <span>{{ item.lastName }}</span>
        </span>
      </template>

      <template v-slot:item.kioskName="{ item }">
        <span class="text-left">{{ item.kioskName }}</span>
      </template>

      <template v-slot:item.facilityName="{ item }">
        <span class="text-left">{{ item.facilityName }}</span>
      </template>

      <template v-slot:item.dashboard="{ item }">
        <v-btn
          :color="getKioskTypeColor(item.kioskTypeId)"
          small
          :loading="isLoadingDashboard && selectedResidentId === item.id"
          :disabled="isLoadingDashboard"
          :dark="!isLoadingDashboard"
          @click="gotoDashboard(item)"
          >
          <v-icon>mdi-view-dashboard</v-icon>
        </v-btn>
      </template>

      <template v-slot:item.createDate="{ item }">
        <span class="text-left">
          <app-date :date="item.createDate" :format="'MMM-DD-YYYY'" />
        </span>
      </template>

      <template v-slot:item.action="{ item }">
        <v-icon @click="remove(item)">delete</v-icon>
      </template>
    </v-data-table>

    <!-- Edit Dialog-->
    <v-dialog v-model="editDialog" max-width="400px" :persistent="true">
      <v-card>
        <v-toolbar dense dark flat :color="toolbarColor">
          <v-toolbar-title>
            <v-icon class="mr-2">edit</v-icon> {{ formTitle }}
          </v-toolbar-title>
          
          <v-spacer />

        </v-toolbar>

        <v-card-text class="pa-1 pt-2">
          <v-container grid-list-md class="pt-0 pb-0">
            <form>
              <v-row no-gutters>
                <v-list-item v-if="editedResident.id > 0 && !isLoadingPictures" class="pa-0 mb-2">
                  <v-list-item-avatar tile size="100" class="pa-0">
                    <v-img :src="editedResident.picture"></v-img>
                  </v-list-item-avatar>

                  <v-spacer />

                  <v-list-item-icon>
                    <v-btn
                      v-if="editedIndex >= 0"
                      @click="changePicture"
                      dark
                      large
                      :color="toolbarColor"
                      class="rounded-0 mt-n2"
                      >
                      <span v-if="editedResident.picture === imageUri_NoAvatar">Add Picture</span>
                      <span v-else>Change Picture</span>
                    </v-btn>
                    <input
                      v-show="false"
                      type="file"
                      accept="image/*"
                      ref="picture"
                      @change="uploadPicture()"
                    />
                  </v-list-item-icon>
                </v-list-item>

                <!-- Kiosk -->
                <v-col cols="12" xs="12">
                  <kiosk-combobox
                    v-model="editedResident.kioskId"
                    :options="kiosksForPicker"
                    :placeholder="'Select kiosk'"
                    :label="getExpression('Label', 'Kiosk')"
                    v-validate="'required'"
                    :error="errors.first('kiosk')"
                    data-vv-name="kiosk"
                    ref="kiosk"
                    class="mb-4"
                  />
                </v-col>

                <!-- First Name -->
                <v-col cols="12" xs="12">
                  <v-text-field
                    v-model="editedResident.firstName"
                    label="First Name"
                    v-validate="'required|max:50'"
                    :error-messages="errors.collect('firstName')"
                    data-vv-name="firstName"
                    ref="firstName"
                  />
                </v-col>

                <!-- Last Name -->
                <v-col cols="12" xs="12">
                  <v-text-field
                    v-model="editedResident.lastName"
                    label="Last Name"
                    v-validate="'max:50'"
                    :error-messages="errors.collect('lastName')"
                    data-vv-name="lastName"
                  />
                </v-col>

                <!-- Language -->
                <v-col cols="12" :sm="isAbby ? '4' : '6'">
                  <v-select
                    v-model="editedResident.languageId"
                    :items="languages"
                    item-value="id"
                    item-text="description"
                    label="Language"
                  />
                </v-col>

                <!-- Beacon/Key # -->
                <v-col cols="12" :sm="isAbby ? '4' : '6'">
                  <v-text-field
                    v-model="editedResident.bluetoothBeaconId"
                    type="number"
                    :label="beaconIdLabel"
                    v-validate="'required:numeric'"
                    :error-messages="errors.collect('beaconId')"
                    data-vv-name="beaconId"
                    class="ml-2"
                  />
                </v-col>

                <!-- Game Difficulty Level (Abby) -->
                <template v-if="isAbby">
                  <!-- <v-col cols="12" sm="3">
                    <v-select
                      v-model="editedResident.gameDifficultyLevel"
                      :items="[1, 2, 3]"
                      label="Game Level"
                      class="ml-2"
                    />
                  </v-col> -->
                  <v-col cols="12" sm="4">
                    <v-select
                      v-model="editedResident.kioskKeyTagId"
                      :items="kioskKeyNums"
                      item-value="id"
                      item-text="keyNum"
                      :disabled="kioskKeyNums.length === 1"
                      label="Key #"
                      class="ml-2"
                    />
                  </v-col>
                </template>
              </v-row>
            </form>
          </v-container>
        </v-card-text>
        <v-card-actions class="pb-4">

          <v-spacer />

          <v-btn @click="closeDialog" :loading="isSaving" class="rounded-0" large>Cancel</v-btn>
          <v-btn :color="toolbarColorDark" dark @click="save" :loading="isSaving" class="rounded-0 ml-4" large>Save</v-btn>

        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import appService from '@/app.service.js'
import { mapGetters } from 'vuex'
import KioskCombobox from '@/components/KioskCombobox'
import ResidentTypeSelector from '@/components/ResidentTypeSelector'
import { AppColor, AppStyle, ImageUri, KioskTypeId, UserRole, FunctionalUserRole } from '@/config.js'
import AppDate from '@/components/App/AppDate'
import AppBackButton from '@/components/App/AppBackButton'

export default {
  name: 'ResidentManager',
  components: {
    KioskCombobox,
    ResidentTypeSelector,
    AppDate,
    AppBackButton
  },
  $_veeValidate: {
    validator: 'new'
  },
  data() {
    return {
      // colors
      appColor_Medium: AppColor.Medium,
      appColor_Dark: AppColor.Dark,
      appColor_Progress: AppColor.Progress,
      appColor_Abby: AppColor.KioskTypeAbbyLight,
      appColor_AbbyDark: AppColor.KioskTypeAbby,
      appColor_Genie: AppColor.KioskTypeGenieLight,
      appColor_GenieDark: AppColor.KioskTypeGenie,
      appColor_ResidentLight: AppColor.ResidentLight,
      appColor_ResidentDark: AppColor.ResidentDark,
      appColor_HyperLink: AppColor.HyperLink,

      // styles
      appStyle_HyperLink: AppStyle.HyperLink,

      // kiosk types
      kioskTypeId_Abby: KioskTypeId.Abby,
      kioskTypeId_Genie: KioskTypeId.Genie,

      // roles
      userRole_Admin: UserRole.Admin,
      userRole_Manager: UserRole.Manager,
      userRole_Guest: UserRole.Guest,
      functionalRole_ResidentManager: FunctionalUserRole.ResidentManager,
      functionalRole_KioskManager: FunctionalUserRole.KioskManager,
      functionalRole_DomainManager: FunctionalUserRole.DomainManager,

      // image uris
      imageUri_NoAvatar: ImageUri.NoAvatar,

      isLoading: true,
      isLoadingKioskOptions: false,
      isLoadingPictures: false,
      isLoadingDashboard: false,
      isSaving: false,
      isDeleting: false,
      search: '',
      editDialog: false,
      residentTypeSelectorDialog: false,
      residents: [],
      kiosks: [],
      facilities: [],
      kioskKeyTags: [],
      kioskOptions: [],
      kiosksForFilter: [],
      kiosksForPicker: [],
      kioskTypes: [],
      kioskTypesForFilter: [],
      kioskId: null,
      kioskTypeId: 0,
      pictures: null,
      selectedResidentId: null,

      editedIndex: -1,
      editedResident: {
        id: -1,
        kioskId: null,
        kioskTypeId: null,
        firstName: null,
        lastName: null,
        picture: ImageUri.NoAvatar,
        bluetoothBeaconId: null,
        uwbTagId: null,
        kioskKeyTagId: null,
        languageId: 1,
        createDate: null,

        // abby
        gameDifficultyLevel: 1
      },
      defaultResident: {
        id: -1,
        kioskId: null,
        kioskTypeId: null,
        firstName: null,
        lastName: null,
        picture: ImageUri.NoAvatar,
        bluetoothBeaconId: null,
        uwbTagId: null,
        kioskKeyTagId: null,
        languageId: 1,
        createDate: null,

        // abby
        gameDifficultyLevel: 1
      },
      restoreResident: {
        id: -1,
        kioskId: null,
        kioskTypeId: null,
        firstName: null,
        lastName: null,
        picture: ImageUri.NoAvatar,
        bluetoothBeaconId: null,
        uwbTagId: null,
        kioskKeyTagId: null,
        languageId: 1,
        createDate: null,

        // abby
        gameDifficultyLevel: 1
      },
      dictionary: {
        custom: {
          firstName: {
            required: () => 'Value is required',
            max: 'Value cannot exceed 50 characters'
          },
          lastName: {
            max: 'Value cannot exceed 50 characters'
          },
          kiosk: {
            required: () => 'Selection is required'
          },
          beaconId: {
            required: () => 'Value is required',
            numeric: 'Value must be numeric'
          },
          uwbTagId: {
            max: 'Value cannot exceed 10 characters'
          }
        }
      }
    }
  },
  computed: {
    ...mapGetters([
      'profile',
      'languages',
      'activeKioskItem',
      'isAdmin',
      'isKioskManager',
      'isResidentManager',
      'routeStack',
      'currentRoute',
      'locale']),
    noDataText () {
      if (this.isLoading) return 'One moment...'
      return this.residents.length === 0 ? 'No residents found' : ''
    },
    title () {
      return 'Residents'
    },
    formTitle () {
      return this.editedResident.id < 0 ? 'New Resident' : 'Edit Resident'
    },
    toolbarColor () {
      return this.appColor_Medium
    },
    toolbarColorDark () {
      return this.appColor_Dark
    },
    beaconIdLabel () {
      return this.isAbby
        ? 'Beacon ID'
        : 'Key/Card Id'
    },
    isFromKioskDashboard () {
      return this.isKioskManager && this.profile.kioskIds.length === 1
        ? this.routeStack.includes('Home')
        : this.routeStack.includes('KioskDashboard')
    },
    headers () {
      var headers = []
      headers.push({
        value: 'picture',
        align: 'center',
        width: '5%',
        sortable: false
      })
      headers.push({
        text: 'First Name',
        value: 'firstName',
        align: 'left',
        width: '15%',
        class: 'pr-0'
      })
      headers.push({
        text: 'Last Name',
        value: 'lastName',
        align: 'left',
        width:  '17%',
        class: 'pr-0'
      })
      headers.push({
        text: 'Kiosk',
        value: 'kioskName',
        align: 'left',
        width: '16%',
        class: 'pr-0'
      })
      headers.push({
        text: 'Facility',
        value: 'facilityName',
        align: 'left',
        width: '17%',
        class: 'pr-0'
      })
      headers.push({
        value: 'dashboard',
        sortable: false,
        align: 'center',
        width: '5%'
      })
      headers.push({
        text: 'Created',
        value: 'createDate',
        align: 'left',
        width: '15%',
        class: 'pr-0'
      })
      headers.push({
        value: 'action',
        sortable: false,
        width: '5%',
        class: 'pl-0'
      })
      return headers
    },
    filteredResidents () {
      if (this.residents === null) return []

      const kioskId = this.kioskId
      const typeId = this.kioskTypeId
      return this.residents.filter(r => {
        return (
          (r.kioskId === kioskId || kioskId < 0) &&
          (r.kioskTypeId === typeId || typeId === 0)
        )
      })
    },
    filteredKiosksForFilter () {
      if (this.kiosksForFilter.length === 0) return []

      const self = this
      const filterKiosks = self.kioskTypeId > 0
        ? self.kiosksForFilter.filter(k => k.typeId === self.kioskTypeId || k.id === -1)
        : self.kiosksForFilter

      return filterKiosks
    },
    isAbby () {
      return this.editedResident.kioskTypeId === this.kioskTypeId_Abby
    },
    kioskKeyNums () {
      if (this.editedResident.kioskId === null) return

      const self = this
      const selectionNone = {
        id: -1,
        keyNum: 'None',
        kioskId: self.editedResident.kioskId
      }

      var keyNums = []
      keyNums.push(selectionNone)
      
      return keyNums.concat(self.kioskKeyTags
        .filter(kt => kt.kioskId === self.editedResident.kioskId
          && (kt.residentId === null || kt.residentId === self.editedResident.id)))
        .map(kt => { return {
          id: kt.id,
          keyNum: kt.keyNum,
          kioskId: kt.kioskId
        }})
    },
    disableKioskFilter () {
      return this.kiosks.length <= 1 || this.isLoading
    },
    disableKioskTypeFilter () {
      return this.kioskTypes.length <= 1 || this.isLoading
    },
    showKioskType () {
      return this.isAdmin && !this.isFromKioskDashboard
    },
    showKiosk() {
      return this.isAdmin
    },
    titleClass () {
      return this.isSmallDevice
        ? 'body-2'
        : ''
    },
    searchColWidth () {
      return this.showKioskType 
        ? this.showKiosk
          ? '6'
          : '8'
        : this.showKiosk
          ? '8'
          : '12'
    }
  },
  methods: {
    loadData () {
      if (!this.isValidSession) return

      const self = this
      self.isLoading = true
      appService.getResidentsForManager().then(data => {
        if (data.errorSeverity > 0) {
          self.handleError(data)
        } else {

          // kiosks
          self.kiosks = data.kiosks
          if (self.kiosks.length == 1) {
            self.kioskId = self.kiosks[0].id
          }

          // kiosk types
          self.kioskTypes = data.kioskTypes
          self.kioskTypes.unshift({ id: 0, name: `(${self.getExpression('Label', 'All')})` })
          data.kioskTypes.forEach(kt => {
            self.kioskTypesForFilter.push(kt)
          })

          // facilities
          self.facilities = data.facilities

          // kiosk key tags
          self.kioskKeyTags = data.kioskKeyTags

          // residents
          self.residents = data.residents.map(r => { return {
            id: r.id,
            name: r.name,
            firstName: r.firstName,
            lastName: r.lastName,
            kioskTypeId: r.kioskTypeId,
            picture: r.picture,
            pictureBase64: r.pictureBase64,
            languageId: r.languageId,
            facilityId: r.facilityId,
            kioskId: r.kioskId,
            bluetoothBeaconId: r.bluetoothBeaconId,
            container: r.container,
            gameDifficultyLevel: r.gameDifficultyLevel,
            gender: r.gender,
            uwbTagId: r.uwbTagId,
            kioskKeyTagId: r.kioskKeyTagId,
            createDate: r.createDate,
            updateDate: r.updateDate,
            kioskName: self.getKioskName(r.kioskId),
            facilityName: self.getFacilityName(r.facilityId)
          }})
          
          // resident pictures
          self.loadPictures()
          self.loadKioskComboOptions()
        }
        self.isLoading = false
      })
    },

    loadKioskComboOptions () {
      const self = this

      self.isLoadingKioskOptions = true
      appService.getKioskComboboxOptions().then(data => {
        if (data.errorSeverity > 0) {
          self.handleError(data)
        } else {
          self.kioskOptions = data.options
          var clonedOptions = data.options.map(k => { return {
            id: k.id,
            name: k.name,
            typeId: k.typeId
          }})
          clonedOptions.unshift({
            id: -1,
            name: '(All)'
          })
          self.kiosksForFilter = clonedOptions
          self.kioskId = -1
        }
        self.isLoadingKioskOptions = false
      })
    },

    edit (item) {
      const self = this

      self.editedIndex = self.residents.findIndex(r => r.id === item.id)
      self.restoreResident = Object.assign({}, item)
      self.editedResident = Object.assign({}, item)
      self.kiosksForPicker = self.kioskOptions.filter(k => k.typeId === self.editedResident.kioskTypeId)
      if (self.editedResident.kioskKeyTagId === null)
        self.editedResident.kioskKeyTagId = -1
      
      self.editDialog = true
      
      // force the kiosk combobox to initialize its watcher
      self.editedResident.kioskId = 0
      setTimeout(()=> {
        self.editedResident.kioskId = item.kioskId
      }, 100)
    },

    remove (item) {
      const self = this
      const index = self.residents.findIndex(r => r.id === item.id)
      if (index < 0) return
      
      const name = self.residents[index].lastName !== null
        ? `${self.residents[index].firstName} ${self.residents[index].lastName}`
        : self.residents[index].firstName

      const kioskName = self.residents[index].kioskName
      const facilityName = self.residents[index].facilityName ?? 'None Selected'

      self.$confirm.show({
        title: 'Delete Resident?',
        text: `<b>${name}</b></br><span>Kiosk: ${kioskName}</span></br><span>Facility: ${facilityName}</span>`,
        onConfirm: () => {
          self.isDeleting = true
          const updateByUserId = self.profile.id.replace('auth0|', '')

          appService.deleteResident(item.id, updateByUserId).then(data => {
            if (data.errorSeverity > 0) {
              self.handleError(data)
            } else {
              appService.removeUserResident(item.id).then((data) => {
                self.isDeleting = false
                if (data.errorSeverity > 0) {
                  self.handleError(data)
                } else {
                  self.residents.splice(index, 1)
                }
              }).catch(e => {
                self.handleCriticalError(e)
              })
            }
          }).catch(e => { self.handleCriticalError(e) })
        }
      })
    },

    save () {
      const self = this
      self.$validator.resume()
      self.$validator.validateAll().then(valid => {
        if (valid) {
          const isEditMode = self.editedIndex > -1
          if (isEditMode) {
            self.isSaving = true
            var editedResident = self.residents[self.editedIndex]
            Object.assign(editedResident, self.editedResident)
            const model = self.getModel(editedResident)
            appService.updateResident(editedResident.id, model, false, self.locale).then(data => {
              if (data.errorSeverity !== null) {
                self.handleError(data)
                Object.assign(self.residents[self.editedIndex], self.restoreResident)
              } else {
                self.closeDialog()
                self.residents[self.editedIndex].kioskName = self.getKioskName(data.resident.kioskId)
                self.residents[self.editedIndex].facilityName = self.getFacilityName(data.resident.facilityId)
              }
              self.isSaving = false
              self.$validator.pause()
            }).catch(e => { self.handleCriticalError(e) })
          } else {
            self.isSaving = true
            const model = self.getModel(self.editedResident)
            appService.addResident(model).then(data => {
              if (data.errorSeverity !== null) {
                self.handleError(data)
              } else {
                self.closeDialog()
                var editedResident = Object.assign({}, data.resident)
                editedResident.kioskName = self.getKioskName(editedResident.kioskId)
                editedResident.facilityName = self.getFacilityName(editedResident.facilityId)
                editedResident.picture = self.imageUri_NoAvatar
                self.residents.unshift(editedResident)
              }
              self.isSaving = false
              self.$validator.pause()
            }).catch(e => { self.handleCriticalError(e) })
          }
        }
      })
    },

    getModel (r) {

      // cast bluetoothBeaconId as Int
      var bluetoothBeaconId = null
      if (r.bluetoothBeaconId !== null) {
        bluetoothBeaconId = isNaN(r.bluetoothBeaconId) ? null : parseInt(r.bluetoothBeaconId)
      }

      // convert 0-length strings to null
      // var uwbTagId = null
      // if (r.uwbTagId !== null) {
      //   uwbTagId = r.uwbTagId.length > 0
      //     ? r.uwbTagId
      //     : null
      // }

      // refresh this in case the kiosk assignment was changed
      const isAbby = r.kioskTypeId === this.kioskTypeId_Abby

      // derive facilityId
      const kiosks = this.kiosks.filter(k => k.id === r.kioskId)
      const facilityId = kiosks.length > 0 ? kiosks[0].facilityId : null
        
      var model = {
        firstName: r.firstName,
        lastName: r.lastName,
        kioskTypeId: r.kioskTypeId,
        kioskId: r.kioskId,
        facilityId: facilityId,
        bluetoothBeaconId: bluetoothBeaconId,
        uwbTagId: r.uwbTagId,
        kioskKeyTagId: r.kioskKeyTagId > 0 ? r.kioskKeyTagId : null,
        languageId: r.languageId,
        createDate: r.createDate ?? new Date(),

        // abby
        gender: isAbby ? 'M' : null,
        gameDifficultyLevel: isAbby
          ? r.gameDifficultyLevel ?? 1
          : null,

        updateByUserId: this.profile.id
      }

      if (this.editedResident.id > 0) {
        model.id = r.id
        model.createDate = r.createDate
      }

      return model
    },

    closeDialog () {
      const self = this
      self.editDialog = false
      setTimeout(() => {
        self.resetEditedResident()
      }, 300)
    },

    closeChooseResidentType (kioskTypeId) {
      const self = this
      self.residentTypeSelectorDialog = false
      self.editedResident.kioskId = -1
      if (kioskTypeId > 0) {
        self.editedResident.kioskTypeId = kioskTypeId
        self.kiosksForPicker = self.kioskOptions.filter(k => k.typeId === kioskTypeId)
        self.editDialog = true
        setTimeout(() => {
          self.editedResident.kioskId = null
        }, 200)
      }
    },

    resetEditedResident () {
      this.editedResident = Object.assign({}, this.defaultResident)
      this.editedIndex = -1
      this.$validator.reset()
      this.$validator.pause()
    },

    loadPictures () {
      const self = this
      self.isLoadingPictures = true
      const ids = !self.isAdmin
        ? self.residents.map(r => r.id)
        : null
      
      if (ids !== null) {
        appService.getResidentPicturesByIds(ids).then((data) => {
          self.pictures = data.pictures
          self.populatePictures()
        })
      } else {
        appService.getResidentPictures().then((data) => {
        self.pictures = data.pictures
        self.populatePictures()
      })
      }
    },

    populatePictures () {
      const self = this
      self.residents.forEach((r) => {
        r.picture = self.getPicture(r.id)
      })
      self.isLoadingPictures = false
    },

    getPicture (id) {
      if (this.pictures === null) return ''
      const self = this
      var pictures = self.pictures.filter((value) => {
        return value.residentId === id
      })
      if (pictures.length > 0) {
        return pictures[0].base64
      }
      return this.imageUri_NoAvatar
    },

    changePicture () {
      this.$refs.picture.click()
    },

    uploadPicture () {
      const self = this

      const file = self.$refs.picture.files[0]
      if (file === undefined) return

      let formData = new FormData()
      formData.append('file', file)
      formData.append('residentId', self.editedResident.id)
      formData.append('updateByUserId', self.profile.id)
      
      self.$upload.execute({
        formData: formData,
        url: 'residents/picture',
        fileCount: 1,
        onComplete: (data) => {
            
          if (data.errorSeverity > 0) {
            self.handleError(data)
          } else {
            self.editedResident.picture = data.base64
          }
        }
      })
    },

    customFilter (value, search, item) {
      const firstName = item.firstName ?? ''
      const lastName = item.lastName ?? ''
      const kioskName = item.kioskName ?? ''
      const facilityName = item.facilityName ?? ''

      return (
        value != null &&
        search != null &&
        ( firstName.toLowerCase()
            .indexOf(search.toLowerCase()) !== -1 ||
          lastName.toLowerCase()
            .indexOf(search.toLowerCase()) !== -1 ||
          kioskName.toLowerCase()
            .indexOf(search.toLowerCase()) !== -1 ||
          facilityName.toLowerCase()
            .indexOf(search.toLowerCase()) !== -1)
      )
    },

    gotoDashboard (item) {
      const self = this
      self.isLoadingDashboard = true
      self.selectedResidentId = item.id
      appService.getResidentExtended(item.id).then(data => {
        if (data.errorSeverity !== null) {
          this.handleError(data)
        } else {
          var resident = data.resident
          this.$store.dispatch('clearActiveResident')
          this.$store.dispatch('setActiveResident', { activeResident: resident })
          this.$store.dispatch('pushRoute', { name: 'ResidentDashboard' })
          this.$router.push({ name: 'ResidentDashboard' })
        }
        self.isLoadingDashboard = false
      })
    },

    getFacilityName (id) {
      const facilities = this.facilities.filter(value => {
        return value.id === id
      })
      return facilities.length > 0 ? facilities[0].name : null
    },

    getKioskName (kioskId) {
      const kiosks = this.kiosks.filter(k => {
        return k.id === kioskId
      })
      return kiosks.length > 0
        ? kiosks[0].serialNumber
        : null
    },

    getKioskTypeColor (typeId) {
      if (typeId === this.kioskTypeId_Abby) {
        return this.appColor_AbbyDark
      } else if (typeId === this.kioskTypeId_Genie) {
        return this.appColor_Genie
      } else return this.appColor_ResidentDark
    },

    handleError(result) {
      this.$error.show({
        title: result.errorSeverity === 0
          ? 'Validation Failed'
          : 'Internal Error',
        text: result.errorMessage,
        severity: result.errorSeverity
      })
    },

    // events

    kioskTypeFilterChanged () {
      this.kioskId = -1
    }
  },
  created() {
    this.loadData()
  },
  mounted() {
    this.$validator.localize('en', this.dictionary)
    this.$validator.reset()
    this.$validator.pause()
  },
  watch: {
    kioskId (val) {
      if (val === null) {
        this.kioskId = -1
      }
    },

    editDialog (val) {
      if (val) {
        const self = this
        setTimeout(() => {
          if (self.editedResident.id < 0) {
            self.$refs.kiosk.focus()
            self.$validator.reset()
            self.$validator.pause()
          }
        }, 300)
      }
    },

    isValidSession(val) {
      if (val === null) return
      this.loadData()
    }
  }
}
</script>
